import React, { useEffect, useState } from 'react';
import { telecallers } from '../../services/dealerApi';
import { UserPlus, Trash2, KeyRound, Power, Loader2, Headphones } from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_OPTIONS = ['CarDekho', 'CarWale', 'CarTrade', 'OLX', 'Droom', 'Spinny', 'Website', 'Walk-in', 'Other'];

export default function TelecallerManager({ onChange } = {}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', source_tag: '' });

  const load = () => {
    setLoading(true);
    telecallers.list()
      .then((r) => setList(r.telecallers || []))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const notify = () => { if (typeof onChange === 'function') onChange(); };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast.error('Name, email and password are all required');
      return;
    }
    setBusy(true);
    try {
      await telecallers.create({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        source_tag: form.source_tag || null,
      });
      toast.success('Telecaller added');
      setForm({ name: '', email: '', password: '', source_tag: '' });
      load();
      notify();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create telecaller');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (tc) => {
    try {
      await telecallers.update(tc.id, { is_active: !tc.is_active });
      toast.success(tc.is_active ? 'Telecaller deactivated' : 'Telecaller activated');
      load();
      notify();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const resetPassword = async (tc) => {
    const pwd = window.prompt(`Enter a new password for ${tc.name}:`);
    if (!pwd || pwd.length < 6) {
      if (pwd) toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await telecallers.resetPassword(tc.id, pwd);
      toast.success('Password reset');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset');
    }
  };

  const removeTc = async (tc) => {
    if (!window.confirm(`Delete ${tc.name}? Their uploaded leads stay under this account but they can no longer log in.`)) return;
    try {
      await telecallers.remove(tc.id);
      toast.success('Telecaller deleted');
      load();
      notify();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="rounded-2xl bg-slate-900/40 border border-slate-800 p-5" data-testid="telecaller-manager">
      <div className="flex items-center gap-2 mb-4">
        <Headphones className="w-5 h-5 text-cyan-400" />
        <h3 className="text-lg font-bold text-white">Telecallers</h3>
        <span className="ml-auto text-xs text-slate-500">Each telecaller sees only leads they upload.</span>
      </div>

      {/* Create form */}
      <form onSubmit={handleCreate} className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-5" data-testid="create-telecaller-form">
        <input
          type="text"
          placeholder="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="tc-name-input"
          className="col-span-1 px-3 py-2 rounded-lg bg-slate-950/70 border border-slate-700 text-white text-sm placeholder-slate-500 focus:border-cyan-500 outline-none"
        />
        <input
          type="email"
          placeholder="email@example.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="tc-email-input"
          className="col-span-1 px-3 py-2 rounded-lg bg-slate-950/70 border border-slate-700 text-white text-sm placeholder-slate-500 focus:border-cyan-500 outline-none"
        />
        <input
          type="text"
          placeholder="Password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          data-testid="tc-password-input"
          className="col-span-1 px-3 py-2 rounded-lg bg-slate-950/70 border border-slate-700 text-white text-sm placeholder-slate-500 focus:border-cyan-500 outline-none"
        />
        <select
          value={form.source_tag}
          onChange={(e) => setForm({ ...form, source_tag: e.target.value })}
          data-testid="tc-source-input"
          className="col-span-1 px-3 py-2 rounded-lg bg-slate-950/70 border border-slate-700 text-white text-sm focus:border-cyan-500 outline-none"
        >
          <option value="">Source (optional)</option>
          {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button
          type="submit"
          disabled={busy}
          data-testid="tc-create-btn"
          className="col-span-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-bold disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add
        </button>
      </form>

      {/* List */}
      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 text-cyan-500 animate-spin" /></div>
      ) : list.length === 0 ? (
        <div className="py-8 text-center text-slate-500 text-sm">No telecallers yet. Add one above.</div>
      ) : (
        <div className="space-y-2" data-testid="telecaller-list">
          {list.map((tc) => (
            <div key={tc.id} className={`flex flex-wrap items-center gap-3 p-3 rounded-xl border ${tc.is_active ? 'border-slate-700 bg-slate-950/40' : 'border-slate-800 bg-slate-950/20 opacity-70'}`} data-testid={`tc-row-${tc.id}`}>
              <div className="flex-1 min-w-[160px]">
                <div className="text-white font-semibold">{tc.name}</div>
                <div className="text-xs text-slate-500">{tc.email}</div>
              </div>
              {tc.source_tag && (
                <span className="px-2 py-1 rounded-full bg-cyan-500/10 text-cyan-300 text-xs">{tc.source_tag}</span>
              )}
              <span className={`px-2 py-1 rounded-full text-xs ${tc.is_active ? 'bg-emerald-500/10 text-emerald-300' : 'bg-slate-700/30 text-slate-400'}`}>
                {tc.is_active ? 'Active' : 'Inactive'}
              </span>
              <div className="flex gap-1">
                <button onClick={() => toggleActive(tc)} title={tc.is_active ? 'Deactivate' : 'Activate'} className="p-2 rounded-lg text-slate-400 hover:text-amber-300 hover:bg-white/5" data-testid={`tc-toggle-${tc.id}`}>
                  <Power className="w-4 h-4" />
                </button>
                <button onClick={() => resetPassword(tc)} title="Reset password" className="p-2 rounded-lg text-slate-400 hover:text-cyan-300 hover:bg-white/5" data-testid={`tc-reset-${tc.id}`}>
                  <KeyRound className="w-4 h-4" />
                </button>
                <button onClick={() => removeTc(tc)} title="Delete" className="p-2 rounded-lg text-slate-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`tc-delete-${tc.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
